import React, { useEffect, useState } from "react";
import { Navigate, Outlet, useParams } from "react-router-dom";
import { useSitesStore } from "@/store/sitesStore";
import { getSites } from "@/services/sitesService";

// Owns the URL:siteId -> localStorage.accountId sync. All data-fetching pages read
// localStorage.accountId, so scoping it to the URL site here makes them follow the
// URL instead of users.site_id. Children mount only after the sync completes.
const SiteProtectedRoute: React.FC = () => {
  const { siteId } = useParams();
  const { selectedSite, setSelectedSite } = useSitesStore();
  const [status, setStatus] = useState<"checking" | "ok" | "denied">("checking");

  useEffect(() => {
    let cancelled = false;
    const urlSiteId = Number(siteId);

    if (!urlSiteId || Number.isNaN(urlSiteId)) {
      setStatus("denied");
      return;
    }

    // Already synced to this URL site — just make sure accountId is set.
    if (selectedSite?.siteId === urlSiteId) {
      localStorage.setItem("accountId", String(urlSiteId));
      setStatus("ok");
      return;
    }

    // Validate the URL site against the user's accessible sites, then sync.
    (async () => {
      try {
        const sites = await getSites();
        if (cancelled) return;
        const match = sites.find((s) => s.siteId === urlSiteId);
        if (!match) {
          setStatus("denied");
          return;
        }
        setSelectedSite(match);
        localStorage.setItem("accountId", String(urlSiteId));
        setStatus("ok");
      } catch {
        if (!cancelled) setStatus("denied");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [siteId, selectedSite, setSelectedSite]);

  if (status === "checking") return null; // brief; could render a spinner
  if (status === "denied") return <Navigate to="/sites" replace />;
  return <Outlet />;
};

export default SiteProtectedRoute;
